import { Lock, RotateCcw, Truck } from 'lucide-react';
import { getCachedStorefrontConfigSync } from '../../utils/storefrontConfig';

interface Item {
  icon: React.ReactNode;
  text: string;
}

/**
 * Trust items. The free-shipping line is tenant-driven: it only appears when
 * the tenant has set a free-shipping threshold (storefrontConfig
 * .freeShippingThresholdInt, in cents). When unset, no shipping promise is
 * made — avoids advertising something the store doesn't actually offer.
 */
function buildDefaultItems(): Item[] {
  const items: Item[] = [
    { icon: <Lock className="w-3.5 h-3.5" aria-hidden="true" />,      text: 'Secure checkout' },
    { icon: <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />, text: '30-day returns' },
  ];

  const thresholdInt = Number(getCachedStorefrontConfigSync()?.freeShippingThresholdInt);
  if (Number.isFinite(thresholdInt) && thresholdInt > 0) {
    const rm = thresholdInt / 100;
    const label = Number.isInteger(rm) ? `RM${rm}` : `RM${rm.toFixed(2)}`;
    items.push({
      icon: <Truck className="w-3.5 h-3.5" aria-hidden="true" />,
      text: `Free shipping over ${label}`,
    });
  }

  return items;
}

interface Props {
  items?: Item[];
  className?: string;
}

/**
 * Compact 1-line trust strip. Designed to sit directly under the
 * Add-to-Cart button on the PDP — keeps trust signals adjacent to the
 * conversion event without taking the visual weight of the full
 * TrustBadgesSection (which still lives further down the page).
 *
 * Color follows --card-text/--card-muted tokens so it adapts to dark
 * tenants that override card text colors.
 */
export function TrustRow({ items, className = '' }: Props) {
  const resolvedItems = items ?? buildDefaultItems();
  return (
    <ul
      className={`flex flex-wrap items-center gap-x-4 gap-y-2 text-xs ${className}`}
      style={{ color: 'var(--card-muted, #6b7280)' }}
    >
      {resolvedItems.map((it, i) => (
        <li key={i} className="inline-flex items-center gap-1.5">
          {it.icon}
          <span>{it.text}</span>
        </li>
      ))}
    </ul>
  );
}
